import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LedgerEntryLine } from '../clients/rust-api-client';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading, Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import HomeSelector from '../Components/Custom/HomeSelector';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import { useMember } from '../Hooks/MemberContext';
import ActivityFeed from './ActivityFeed';
import BalanceHero from './BalanceHero';
import EntryDetailDialog from './EntryDetailDialog';
import PayCard from './PayCard';
import AchPayCard from './AchPayCard';
import { useAccount } from './useAccount';

interface Props {
  currentPage: string;
}

/**
 * The account hub — balance, how to pay, recent activity.
 *
 * Modelled on a banking app's home screen rather than on front-porch's
 * `HomeAccountPanel`, which is a tab strip over six manager tools
 * (ledger, statement, coupon, payments, tenures, estoppel). A homeowner
 * arrives with one question — *what do I owe and how do I pay it* — and
 * the page answers it above the fold, then offers history underneath.
 */
const AccountPage: React.FC<Props> = ({ currentPage }) => {
  const navigate = useNavigate();
  const { homes, selectedHome, loading: memberLoading } = useMember();
  const homeId = selectedHome?.home_id;
  const { balance, coupon, loading, error, clearError, setError } =
    useAccount(homeId);
  const [selected, setSelected] = useState<LedgerEntryLine | null>(null);

  if (memberLoading) return <LoadingSpinner />;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Heading>Your account</Heading>

      {homes.length === 0 ? (
        <Subheading className="mt-2">
          Your account is not linked to a home yet. Please contact your
          association manager.
        </Subheading>
      ) : (
        <>
          <div className="mt-2">
            <HomeSelector />
          </div>

          <ErrorAlert className="mt-4" onDismiss={clearError}>
            {error}
          </ErrorAlert>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="mt-6 space-y-6">
              <BalanceHero balance={balance} dueDate={coupon?.due_date} />

              {/* Pay by bank account, when the association has turned it
                  on; renders nothing otherwise. Above the coupon because
                  it is the one that needs no stamp. */}
              {homeId && (
                <AchPayCard
                  homeId={homeId}
                  balance={balance?.balance}
                  onError={setError}
                />
              )}

              {homeId && (
                <PayCard homeId={homeId} coupon={coupon} onError={setError} />
              )}

              {homeId && (
                <div>
                  <div className="mb-3 flex items-baseline justify-between">
                    <Subheading>Recent activity</Subheading>
                    <Link
                      to="/account/activity"
                      className="text-sm text-sky-700 hover:underline dark:text-sky-400"
                    >
                      See all
                    </Link>
                  </div>

                  <ActivityFeed
                    homeId={homeId}
                    limit={6}
                    onSelect={setSelected}
                    onError={setError}
                  />
                </div>
              )}

              <div className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-700 dark:bg-gray-800">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Subheading>Statements</Subheading>
                    <Text className="mt-1 text-sm">
                      A month-by-month record you can download and keep.
                    </Text>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/account/statements')}
                    className="shrink-0 text-sm text-sky-700 hover:underline dark:text-sky-400"
                  >
                    View all
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <EntryDetailDialog entry={selected} onClose={() => setSelected(null)} />
    </SidebarLayout>
  );
};

export default AccountPage;
